import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import styles from './OnboardingModal.module.css';

function toEmbedUrl(url: string): string | null {
    if (!url) return null;
    // https://www.youtube.com/watch?v=ID or https://youtu.be/ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    // Already an embed URL
    if (url.includes('/embed/')) return url;
    return null;
}

export function OnboardingModal() {
    const { user, markOnboardingComplete } = useAuth();
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [visible, setVisible] = useState(false);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (!user || user.role === 'ADMIN' || user.onboardingCompletedAt || user.status === 'suspended') return;
        // Aguarda a pesquisa de onboarding (perfil de negócio) ser concluída antes
        // de exibir o vídeo, para contas novas que precisam respondê-la primeiro.
        if (user.surveyOnboardingRequired && !user.surveyOnboardingCompletedAt) return;
        api.getOnboardingConfig().then(({ videoUrl }) => {
            if (videoUrl) {
                setVideoUrl(videoUrl);
                setVisible(true);
            }
        }).catch(() => {/* silently ignore */ });
    }, [user]);

    const handleComplete = useCallback(async () => {
        setConfirming(true);
        try {
            await markOnboardingComplete();
        } finally {
            setConfirming(false);
            setVisible(false);
        }
    }, [markOnboardingComplete]);

    if (!visible || !videoUrl) return null;

    const embedUrl = toEmbedUrl(videoUrl);
    if (!embedUrl) return null;

    return (
        <div className={`${styles.overlay} notranslate`} translate="no">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Bem-vindo! Veja como começar</h2>
                </div>
                <div className={styles.videoWrapper}>
                    <iframe
                        src={embedUrl}
                        title="Vídeo de onboarding"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className={styles.iframe}
                    />
                </div>
                <div className={styles.footer}>
                    <button
                        className={styles.btnPrimary}
                        onClick={handleComplete}
                        disabled={confirming}
                    >
                        {confirming ? 'Aguarde...' : 'Entendido, não mostrar novamente'}
                    </button>
                </div>
            </div>
        </div>
    );
}
